import styled from "@xstyled/styled-components";
import { useFormikContext } from "formik";
import { useMemo, useState } from "react";

import { JobFormFields } from "../../types";
import { AddButton } from "../AddButton";
import { OfficeDays } from "../Location/OfficeDays";
import { OfficeDescription } from "../Location/OfficeDescription";

import { JobEditLocationField } from "./LocationField";
import { RemoteInfo } from "./RemoteInfo";
import { Restrictions } from "./Restrictions";

import { Fieldset, Spacing } from "@otta/design";

const StyledLocationField = styled(JobEditLocationField)`
  margin-top: 19;
`;

export function allRemote(locs?: string[]): boolean {
  return locs?.every(v => v.startsWith("REMOTE_")) ?? true;
}

function useIsRemote(): boolean {
  const {
    values: { locationPreferences },
  } = useFormikContext<JobFormFields>();
  return useMemo(() => allRemote(locationPreferences), [locationPreferences]);
}

/**
 * We show the extra form fields either if you ask for them
 * or if you previously set some info and are editing a job
 */
function useShowExtraFields(): { show: boolean; setShow: () => void } {
  const {
    values: { locationRestrictions, locationInformation },
  } = useFormikContext<JobFormFields>();
  const [show, setShow] = useState(false);
  return useMemo(
    () => ({
      show: show || !!locationRestrictions || !!locationInformation,
      setShow: () => setShow(true),
    }),
    [locationRestrictions, locationInformation, show, setShow]
  );
}

export function Location() {
  const remote = useIsRemote();
  const { show, setShow } = useShowExtraFields();

  return (
    <Fieldset legend="Location" spacing={null}>
      <Spacing size={1}>
        <StyledLocationField />
        {!remote && (
          <Spacing size={1}>
            <OfficeDescription />
            <OfficeDays />
          </Spacing>
        )}
        {show && (
          <Spacing size={1}>
            <RemoteInfo />
            <Restrictions />
          </Spacing>
        )}
        {!show && (
          <AddButton onClick={() => setShow()} isOptional>
            Add location restrictions or other information
          </AddButton>
        )}
      </Spacing>
    </Fieldset>
  );
}
